import { createSelector, PayloadAction, ThunkDispatch } from "@reduxjs/toolkit";
import { getCacheKey } from "../apiRTK";
import { createAppSlice } from "../app/createAppSlice";
import { IAppThunk, IRootState } from "../app/store";
import { PartialMcBimSpace } from "../utils";
import { IFC_CLASSES_CEILING_DEVICES, IfcClassesEnum } from "@multiconsult-group/multiplacer-core";
import { IPixPath, IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import {
  getValidationsForSpace,
  ISpaceValidation,
  SPACE_VALIDATION_ID_ENUM,
} from "../RoomSideBar/SpaceValidations/spaceValidationsUtils";
import { ROOM_COLORING_IDS } from "../RoomColoring";
import {
  IDeviceTypesIfcInstanceClassesEnum,
  IMcBimItem,
  IObjectId,
  IRoomsSummaryHashmap,
} from "@backend/src/shared/schemas/schemas";
import { AccountInfo } from "@azure/msal-browser";
import { openapi, PostMultiplacerDevicesApiArg } from "../../openapi";
import { convertGenereicMlResultToPixPolygon } from "./devices/deviceUtils";
import { DesignLeftSideBarTabs, RoomSideBarTabs } from "@multiconsult-group/multiplacer-core";
import { clearDesignThunk, IRoomMcBimSpaceCombo } from "./designThunk";
import {
  DEFAULT_IFC_CLASSES_TO_DISPLAY,
  loadDisciplinesToDisplay,
  loadIfcClassesToDisplay,
  loadMcBimItemColorMode,
  saveDisciplinesToDisplay,
  saveIfcClassesToDisplay,
  saveMcBimItemColorMode,
  saveMcBimItemsFilterMode,
} from "./localStorageHandler";
import {
  IDevice,
  IMLPredictDevice,
  IMultiplacerModuleKey,
} from "@multiconsult-group/multiplacer-core";
import { buildCeilingGridPixPathsFromLines } from "../buildCeilingGridPixPathsFromLines";

const IFC_CLASSES_CEILING_CONTEXT = Object.freeze([
  ...IFC_CLASSES_CEILING_DEVICES,
  IfcClassesEnum.IFCFURNITURE,
  IfcClassesEnum.IFCWALL,
  IfcClassesEnum.IFCWALLSTANDARDCASE,
  IfcClassesEnum.IFCCOLUMN,
  IfcClassesEnum.IFCWINDOW,
  IfcClassesEnum.IFCDOOR,
]) as readonly string[];

const areCeilingIfcClassesVisible = (ifcClassesToDisplay: IfcClassesToDisplayMap): boolean =>
  IFC_CLASSES_CEILING_CONTEXT.every((ifcClass) => ifcClassesToDisplay[ifcClass] === true);

export interface MlHeatmapTile {
  points: number[][];
  color: [number, number, number, number];
}
import { ICeilingGridItem } from "@multiconsult-group/multiplacer-core";

export enum ETab {
  DESIGN = "DESIGN",
}

interface IToast {
  header: string;
  text: string;
  isSuccess: boolean;
  isOpen: boolean;
}

interface ITrainAll {
  loading: boolean;
  nrOfTrainedRooms: number;
  nrOfFailedRooms: number;
}

export interface ISpaceNumberAndSpaceValidationsResults {
  spaceNumber: string;
  spaceValidations: ISpaceValidation[];
}

export interface ISpaceValidationRun {
  active: boolean;
  loading: boolean;
  spacesValidationsResults: ISpaceNumberAndSpaceValidationsResults[];
}
export type IDeviceViewMode = "devices" | "ifc";

export type IFloorAndJob = {
  floorName: string;
  floorGlobalId: string;
  jobId: string;
  elevation?: number;
} | null;

export interface IBuildingStoryListState {
  search: string;
  sortMode: "name" | "rooms";
}

export interface IAdvancedCopyState {
  selectedTargetRoomNumber: string | null;
  selectedTargetRoomNumbers: string[];
}

export type IfcClassesToDisplayMap = Partial<Record<string, boolean>>;

export type IRfpStandardRoomDiscipline = "standardRoom.E" | "standardRoom.V" | undefined;

export type IMcBimItemsFilterMode = "INSTANCE_CLASS" | "DISCIPLINE";

// Norsk (Bokmål): Generisk map for av/på per nøkkel (kan gjenbrukes for flere filtre senere).
export type ToggleMap = Partial<Record<string, boolean>>;

export type DisciplinesToDisplayMap = ToggleMap;

export type IMcBimItemColorMode =
  | "MULTIPLACER_COLORS"
  | "MMI"
  | "DISCIPLINE_COLORS"
  | "PRIMARY_SYSTEM";

export interface IDesignSliceState {
  activeModule: IMultiplacerModuleKey | undefined;
  accountInfo: (AccountInfo & { accessToken: string }) | null;
  floorAndJob: IFloorAndJob;
  value: number;
  projectNo: number;
  activeTab: ETab;
  mlGenrericSpaceDevices: IMLPredictDevice[];
  algosInProgress: boolean;
  countAlgosSpace: {
    total: number;
    complete: number;
    failed: number;
  };
  highLightStandardSpaces: boolean;
  selectedSpaceNumber: string | undefined;
  copySpaceModeActive: boolean;
  advancedCopy: IAdvancedCopyState;
  selectedRoomTab: RoomSideBarTabs | undefined;
  saveEditedRoomsLoading: boolean;
  toast: IToast;
  trainAll: ITrainAll;
  fillTile: boolean;
  hidePlayButton: boolean;
  hideApp: boolean;
  allowMultiselect: boolean;
  viewerToBeShow: "FLOORVIEWER" | "SINGLEVIEWER";
  hideDevices: boolean;
  searchTerm: string;
  selectedSpaceValidation: SPACE_VALIDATION_ID_ENUM | null;
  activeLeftSideBarTab: DesignLeftSideBarTabs | undefined;
  dragSelectSpaces: boolean;
  selectedRooms: string[];
  shiftSelectRooms: boolean;
  review: {
    active: boolean;
    currIndex: number;
  };
  ifcClassesToDisplay: IfcClassesToDisplayMap;
  showCeilingIfcClassesContext: boolean;
  mcbimItemsFilterMode: IMcBimItemsFilterMode; // ✅ NY
  disciplinesToDisplay: DisciplinesToDisplayMap; // ✅ NY
  deviceTypeInstanceClassFilter: IDeviceTypesIfcInstanceClassesEnum;
  rotation: number;
  showTrueSizeOfDevices: boolean;
  snapToGrid: boolean;
  spaceValidationRun: ISpaceValidationRun;
  activeSpaceValidationSpecs: Record<SPACE_VALIDATION_ID_ENUM, boolean>;
  activeRoomColoringId: ROOM_COLORING_IDS | string | undefined;
  showFireDeviceCircles: boolean;
  showFireDeviceSquare: boolean;
  deviceOnTheMove: IObjectId | undefined;
  measurementToolActive: boolean;
  nonPublischedDevicesFromAlgos: IDevice[];
  mlHeatmapTiles: MlHeatmapTile[];
  deviceViewMode: IDeviceViewMode; // nytt
  buildingStoryList: IBuildingStoryListState;
  showRoomColoringLarge: boolean;
  rfpStandardRoomDiscipline: IRfpStandardRoomDiscipline;
  zoomExtentNonce: number;
  mouseHelpModalOpen: boolean;
  pipeSystemsCopyModalOpen: boolean;
  selectedRoutingGraphId: string | null;
  runSpaceValidationsInSelectedRooms: boolean;
  mcbimItemColorMode: IMcBimItemColorMode;
  selectedBuilding: string | null;
  rfpSpecifiedArticlesResponsibility: string | null;
  showContainerSpaces: boolean;
  showOnlyRoomSectionItemsVisibleIn2d: boolean;
  showRoomSectionPerimeterItems: boolean;
  showCeilingFreeTilesForFloor: boolean;
}

const initialState: IDesignSliceState = {
  activeModule: undefined,
  accountInfo: null,
  floorAndJob: null,
  value: 0,
  projectNo: 0,
  activeTab: (localStorage.getItem("activeTab") as ETab) || ETab.DESIGN,
  mlGenrericSpaceDevices: [],
  algosInProgress: false,
  countAlgosSpace: {
    total: 0,
    complete: 0,
    failed: 0,
  },
  highLightStandardSpaces: false,
  selectedSpaceNumber: undefined, // "2iq$n4e1nCrhzyqoV6EG$J",
  copySpaceModeActive: false,
  advancedCopy: {
    selectedTargetRoomNumber: null,
    selectedTargetRoomNumbers: [],
  },
  selectedRoomTab: undefined,
  saveEditedRoomsLoading: false,
  toast: {
    header: "",
    text: "",
    isSuccess: false,
    isOpen: false,
  },
  trainAll: {
    loading: false,
    nrOfTrainedRooms: 0,
    nrOfFailedRooms: 0,
  },
  fillTile: false,
  hidePlayButton: false,
  hideApp: false,
  allowMultiselect: false,
  viewerToBeShow: "FLOORVIEWER",
  hideDevices: false,
  searchTerm: "",
  selectedSpaceValidation: null,
  activeLeftSideBarTab: undefined,
  dragSelectSpaces: false,
  selectedRooms: [],
  shiftSelectRooms: false,
  review: {
    active: false,
    currIndex: 0,
  },
  ifcClassesToDisplay: loadIfcClassesToDisplay(),
  showCeilingIfcClassesContext: areCeilingIfcClassesVisible(loadIfcClassesToDisplay()),
  mcbimItemsFilterMode:
    (localStorage.getItem("mcbimItemsFilterMode") as IMcBimItemsFilterMode) || "INSTANCE_CLASS",

  disciplinesToDisplay: loadDisciplinesToDisplay(), // ✅ NY (se localStorageHandler
  deviceTypeInstanceClassFilter: IfcClassesEnum.IFCAIRTERMINAL,
  rotation: (Number(localStorage.getItem("rotation")) as number) || 0,
  showTrueSizeOfDevices: false,
  snapToGrid: true,
  spaceValidationRun: {
    loading: false,
    active: false,
    spacesValidationsResults: [],
  },
  activeSpaceValidationSpecs: Object.values(SPACE_VALIDATION_ID_ENUM).reduce(
    (acc, value) => {
      acc[value] = true;
      return acc;
    },
    {} as Record<SPACE_VALIDATION_ID_ENUM, boolean>
  ),
  activeRoomColoringId:
    (localStorage.getItem("activeRoomColoringId") as
      | ROOM_COLORING_IDS
      | string
      | null
      | undefined) ?? undefined,
  showFireDeviceCircles: false,
  showFireDeviceSquare: false,
  deviceOnTheMove: undefined,
  measurementToolActive: false,
  nonPublischedDevicesFromAlgos: [],
  mlHeatmapTiles: [],
  deviceViewMode: (localStorage.getItem("deviceViewMode") as IDeviceViewMode) || "devices",
  buildingStoryList: {
    search: "",
    sortMode: "name",
  },
  showRoomColoringLarge: true,
  rfpStandardRoomDiscipline: localStorage.getItem("rfpStandardRoomDiscipline") as any,
  zoomExtentNonce: 0,
  mouseHelpModalOpen: false,
  pipeSystemsCopyModalOpen: false,
  selectedRoutingGraphId: null,
  runSpaceValidationsInSelectedRooms: false,
  mcbimItemColorMode: loadMcBimItemColorMode(),
  selectedBuilding: null,
  rfpSpecifiedArticlesResponsibility: null,
  showContainerSpaces: false,
  showOnlyRoomSectionItemsVisibleIn2d: false,
  showRoomSectionPerimeterItems: false,
  showCeilingFreeTilesForFloor: false,
};

interface IFetchItemsArgs {
  standardSpaceRoomNr: string;
  roomMcBimSpace: IRoomMcBimSpaceCombo;
}

const fetchStandardSpaceAndItems = async (
  dispatch: ThunkDispatch<IRootState, unknown, PayloadAction<any>>,
  getState: () => IRootState,
  args: IFetchItemsArgs
) => {
  const floorAndJob = getState().design.floorAndJob;
  if (floorAndJob == null) {
    return { standardSpace: null, mcbimItemsInStandardSpace: [], mcbimSpace: null };
  }
  // Hämta standardSpace
  const promiseStandardSpace = dispatch(
    openapi.endpoints.getMcbimSingleMcbimSpace.initiate({
      projectNo: getState().design.projectNo,
      name: args.standardSpaceRoomNr,
    })
  );
  const { data: standardSpace } = await promiseStandardSpace;
  if (standardSpace == null) {
    dispatch(setCountAlgoSpaceFailed(getState().design.countAlgosSpace.failed + 1));
    return { standardSpace: null, mcbimItemsInStandardSpace: [], mcbimSpace: null };
  }

  // Hämta items i standardSpace
  const promiseMcBimItemsInStandardSpace = dispatch(
    openapi.endpoints.getMcbimItemsInSpace.initiate({
      projectNo: getState().design.projectNo,
      spaceId: standardSpace._id,
    })
  );
  const { data: mcbimItemsInStandardSpace = [] } = await promiseMcBimItemsInStandardSpace;

  // Hämta mcbimSpace
  const promiseMcBimSpace = dispatch(
    openapi.endpoints.getMcbimSingleMcbimSpace.initiate({
      projectNo: getState().design.projectNo,
      name: args.roomMcBimSpace.roomNumber,
    })
  );
  const { data: mcbimSpace = [] } = await promiseMcBimSpace;

  return { standardSpace, mcbimItemsInStandardSpace, mcbimSpace };
};

export const runFetchStandardSpaceAndItemsForMultiple = (
  args: IFetchItemsArgs[],
  dispatch: ThunkDispatch<IRootState, unknown, PayloadAction<any>>,
  getState: () => IRootState
) => {
  // TODO: Implement function logic or remove if not needed
  return async () => {
    const chunkSize = 40;
    const results: any[] = [];
    for (let i = 0; i < args.length; i += chunkSize) {
      const chunk = args.slice(i, i + chunkSize);
      const chunkResults = await Promise.all(
        chunk.map((arg) => fetchStandardSpaceAndItems(dispatch, getState, arg))
      );
      results.push(...chunkResults);
    }
    return results;
  };
};

interface IToastArgs {
  text: string;
  header: string;
  isSuccess: boolean;
}

export const thunkToast = (args: IToastArgs): IAppThunk => {
  return (dispatch) => {
    dispatch(
      setToastInRedux({
        text: args.text,
        header: args.header,
        isOpen: true,
        isSuccess: args.isSuccess,
      })
    );
    setTimeout(() => {
      dispatch(
        setToastInRedux({
          text: "",
          header: "",
          isOpen: false,
          isSuccess: args.isSuccess,
        })
      );
    }, 5000);
  };
};

export const thunkChangeStepInReviewMode = (inc: boolean): IAppThunk => {
  return (dispatch, getState) => {
    const { currIndex, active } = getState().design.review;
    if (!active) {
      return;
    }
    const selectedRooms = getState().design.selectedRooms;

    if (inc) {
      const isOnLast = currIndex + 1 === selectedRooms.length;
      if (isOnLast) {
        return;
      }
      dispatch(incCurrIndexReviewModeInRedux());
    } else {
      if (currIndex === 0) {
        return;
      }
      dispatch(decCurrIndexReviewModeInRedux());
    }
    dispatch(setSelectedSpaceNumberInRedux(selectedRooms[currIndex + (inc ? 1 : -1)]));
  };
};

export interface ITrainAllArgs {
  limit: number;
  onlyMissing: boolean;
  prevTraindStandardSpaces: string[];
  standardSpaces: string[];
}

export const getHeightFromFirstCeilingGrid = (
  ceilingGrids: ICeilingGridItem[]
): number | undefined => {
  if (ceilingGrids.length === 0) {
    return undefined;
  }

  const firstGrid = ceilingGrids[0];

  if (firstGrid.floorHeightMm == null) {
    return undefined;
  }

  return Math.round(firstGrid.floorHeightMm / 10) * 10;
};
export const thunkRunSpaceValidations = (changeTab = true): IAppThunk => {
  return async (dispatch, getState) => {
    const floorAndJob = getState().design.floorAndJob;
    if (floorAndJob == null) {
      return;
    }
    dispatch(setSpaceValidationLoadingInRedux(true));
    dispatch(setSpaceValidationRunActiveInRedux(true));
    const queries = getState().api.queries;
    const cacheKeyGetRooms = getCacheKey(queries, "getMultiplacerRooms");
    const respGetRooms = openapi.endpoints.postMultiplacerRooms.select(cacheKeyGetRooms);
    const rooms = respGetRooms(getState()).data as IRoomsSummaryHashmap;
    const activeSpaceValidationSpecs = getState().design.activeSpaceValidationSpecs;

    const cacheKeyGetSpaces = getCacheKey(queries, "getMcbimSpaces");
    const respGetSpaces = openapi.endpoints.getMcbimSpaces.select(cacheKeyGetSpaces);
    const mcbimSpaces = respGetSpaces(getState()).data as PartialMcBimSpace[];

    const respGetDevices = openapi.endpoints.postMultiplacerDevices.select(
      getCacheKey(queries, "postMultiplacerDevices")
    );

    const respGetProject = openapi.endpoints.getMultiplacerProjectsSingleProject.select(
      getCacheKey(queries, "getMultiplacerProjectsSingleProject")
    );

    const project = respGetProject(getState()).data;

    if (project == null) {
      return;
    }
    const devices = respGetDevices(getState()).data as IDevice[];

    const selectedRooms = getState().design.selectedRooms;

    const spaceValidations: ISpaceNumberAndSpaceValidationsResults[] = [];

    for (const roomNumber of selectedRooms) {
      let standardSpacItems: IMcBimItem[] = [];
      const room = rooms[roomNumber];
      const mcbimSpace = mcbimSpaces.find(
        (space) => space.name === roomNumber
      ) as PartialMcBimSpace;
      const devicesInRoom = devices.filter((device) => device.roomNumber === roomNumber);
      const promiseMcBimItemsInSpace = dispatch(
        openapi.endpoints.getMcbimItemsInSpace.initiate({
          projectNo: project.projectNo,
          spaceName: roomNumber,
        })
      );
      const { data: mcbimItemsInSpace = [] } = await promiseMcBimItemsInSpace;

      if (room?.rfpMalRom != null) {
        const promiseMcBimSpaceStandardRoom = dispatch(
          openapi.endpoints.getMcbimSingleMcbimSpace.initiate({
            projectNo: project.projectNo,
            name: room.rfpMalRom,
          })
        );
        let { data: mcbimSpaceStandardRoom } = await promiseMcBimSpaceStandardRoom;
        if (mcbimSpaceStandardRoom != null) {
          const promiseMcBimItemsInStandardRoom = dispatch(
            openapi.endpoints.getMcbimItemsInSpace.initiate({
              projectNo: project.projectNo,
              spaceId: mcbimSpaceStandardRoom._id as string,
            })
          );
          const { data: items } = await promiseMcBimItemsInStandardRoom;
          standardSpacItems = items as IMcBimItem[];
        }
      }

      if (room != null && mcbimSpace != null && devicesInRoom.length > 0) {
        const validations = getValidationsForSpace({
          devicesInRoom,
          singleRoom: room,
          mcbimItemsInStandardSpace: standardSpacItems,
          mcbimItemsInSpace: mcbimItemsInSpace as IMcBimItem[],
          singleSpace: mcbimSpace,
          activeSpaceValidationSpecs,
          compCodePath: project.compCodePath,
        });
        spaceValidations.push({
          spaceNumber: roomNumber,
          spaceValidations: validations,
        });
      }
    }
    dispatch(setSpaceValidationsResultsInRedux(spaceValidations));
    if (changeTab) {
      dispatch(setActiveLeftSideBarTabInRedux(DesignLeftSideBarTabs.CEILING_VALIDATIONS));
    }
    dispatch(setSpaceValidationLoadingInRedux(false));
  };
};

// If you are not using async thunks you can use the standalone `createSlice`.
export const designSlice = createAppSlice({
  name: "design",
  initialState,
  reducers: () => ({
    setFloorAndJobInRedux: (state, action: PayloadAction<IFloorAndJob | null>) => {
      state.floorAndJob = action.payload;
    },
    setActiveTabInRedux: (state, action: PayloadAction<ETab>) => {
      //spara detta i localstorge
      localStorage.setItem("activeTab", action.payload);
      state.activeTab = action.payload;
    },

    setCountAlgoSpaceTotal: (state, action: PayloadAction<number>) => {
      state.countAlgosSpace.total = action.payload;
    },
    setCountAlgoSpaceComplete: (state, action: PayloadAction<number>) => {
      state.countAlgosSpace.complete = action.payload;
    },
    setCountAlgoSpaceFailed: (state, action: PayloadAction<number>) => {
      state.countAlgosSpace.failed = action.payload;
    },
    setAlgosInProgressInRedux: (state, action: PayloadAction<boolean>) => {
      state.algosInProgress = action.payload;
    },
    setHighlightStandardSpacesInRedux: (state, action: PayloadAction<boolean>) => {
      state.highLightStandardSpaces = action.payload;
    },
    setSelectedSpaceNumberInRedux: (state, action: PayloadAction<string | undefined>) => {
      state.selectedSpaceNumber = action.payload;
    },
    setToastInRedux: (state, action: PayloadAction<IToast>) => {
      state.toast = action.payload;
    },
    setTrainAllLoadingInRedux: (state, action: PayloadAction<ITrainAll>) => {
      state.trainAll = action.payload;
    },
    setFillTileInRedux: (state, action: PayloadAction<boolean>) => {
      state.fillTile = action.payload;
    },
    setHidePlayButtonInRedux: (state, action: PayloadAction<boolean>) => {
      state.hidePlayButton = action.payload;
    },
    setHideAppInRedux: (state, action: PayloadAction<boolean>) => {
      state.hideApp = action.payload;
    },
    setAllowMultiselectInRedux: (state, action: PayloadAction<boolean>) => {
      state.allowMultiselect = action.payload;
    },
    setViewerToBeShowInRedux: (state, action: PayloadAction<"FLOORVIEWER" | "SINGLEVIEWER">) => {
      state.viewerToBeShow = action.payload;
    },
    setHideDevicesInRedux: (state, action: PayloadAction<boolean>) => {
      state.hideDevices = action.payload;
    },
    setSearchTermInRedux: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setSelectedRoomTabInRedux: (state, action: PayloadAction<RoomSideBarTabs | undefined>) => {
      state.selectedRoomTab = action.payload;
    },
    setSelectedSpaceValidationInRedux: (
      state,
      action: PayloadAction<SPACE_VALIDATION_ID_ENUM | null>
    ) => {
      state.selectedSpaceValidation = action.payload;
    },
    setActiveLeftSideBarTabInRedux: (
      state,
      action: PayloadAction<DesignLeftSideBarTabs | undefined>
    ) => {
      state.activeLeftSideBarTab = action.payload;
    },
    setDragSelectSpacesInRedux: (state, action: PayloadAction<boolean>) => {
      state.dragSelectSpaces = action.payload;
    },
    setSelectedRoomsInRedux: (state, action: PayloadAction<string[]>) => {
      state.selectedRooms = action.payload;
    },
    addSelectedRoomToRedux: (state, action: PayloadAction<string>) => {
      state.selectedRooms.push(action.payload);
    },
    setMultipleSelectedRoomsToRedux: (state, action: PayloadAction<string[]>) => {
      state.selectedRooms = action.payload;
    },
    removeSelectedRoomFromRedux: (state, action: PayloadAction<string>) => {
      state.selectedRooms = state.selectedRooms.filter((room) => room !== action.payload);
    },
    setShiftSelectRoomsInRedux: (state, action: PayloadAction<boolean>) => {
      state.shiftSelectRooms = action.payload;
    },
    toggleReviewModeInRedux: (state, action: PayloadAction<boolean>) => {
      state.review.active = action.payload;
    },
    incCurrIndexReviewModeInRedux: (state) => {
      state.review.currIndex++;
    },
    decCurrIndexReviewModeInRedux: (state) => {
      state.review.currIndex--;
    },
    resetReviewModeInRedux: (state) => {
      state.review.active = false;
      state.review.currIndex = 0;
    },
    setIfcClassesToDisplayInRedux: (state, action: PayloadAction<string>) => {
      state.ifcClassesToDisplay[action.payload] = !state.ifcClassesToDisplay[action.payload];
      state.showCeilingIfcClassesContext = areCeilingIfcClassesVisible(state.ifcClassesToDisplay);
      saveIfcClassesToDisplay(state.ifcClassesToDisplay);
    },
    setIfcClassVisibilityInRedux: (
      state,
      action: PayloadAction<{ ifcClass: string; visible: boolean }>
    ) => {
      state.ifcClassesToDisplay[action.payload.ifcClass] = action.payload.visible;
      state.showCeilingIfcClassesContext = areCeilingIfcClassesVisible(state.ifcClassesToDisplay);
      saveIfcClassesToDisplay(state.ifcClassesToDisplay);
    },
    toggleCeilingIfcClassesContextInRedux: (state) => {
      const nextVisible = !state.showCeilingIfcClassesContext;
      if (nextVisible) {
        for (const ifcClass of IFC_CLASSES_CEILING_CONTEXT) {
          state.ifcClassesToDisplay[ifcClass] = true;
        }
      } else {
        state.ifcClassesToDisplay = { ...DEFAULT_IFC_CLASSES_TO_DISPLAY };
      }
      state.showCeilingIfcClassesContext = nextVisible;
      saveIfcClassesToDisplay(state.ifcClassesToDisplay);
    },
    setDeviceTypeInstanceClassFilterInRedux: (
      state,
      action: PayloadAction<IDeviceTypesIfcInstanceClassesEnum>
    ) => {
      state.deviceTypeInstanceClassFilter = action.payload;
    },
    setRotationInRedux: (state, action: PayloadAction<number>) => {
      localStorage.setItem("rotation", action.payload.toString());
      state.rotation = action.payload;
    },
    setCopySpaceModeActiveInRedux: (state, action: PayloadAction<boolean>) => {
      state.copySpaceModeActive = action.payload;
    },
    setAdvancedCopySelectedTargetRoomNumberInRedux: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.advancedCopy.selectedTargetRoomNumber = action.payload;
    },
    setAdvancedCopySelectedTargetRoomNumbersInRedux: (state, action: PayloadAction<string[]>) => {
      state.advancedCopy.selectedTargetRoomNumbers = action.payload;
    },
    toggleShowTrueSizeOfDevicesInRedux: (state) => {
      state.showTrueSizeOfDevices = !state.showTrueSizeOfDevices;
    },
    toggleSnapToGridInRedux: (state) => {
      state.snapToGrid = !state.snapToGrid;
    },
    setShowCeilingFreeTilesForFloorInRedux: (state, action: PayloadAction<boolean>) => {
      state.showCeilingFreeTilesForFloor = action.payload;
    },
    setSpaceValidationRunActiveInRedux: (state, action: PayloadAction<boolean>) => {
      state.spaceValidationRun.active = action.payload;
    },
    setSpaceValidationsResultsInRedux: (
      state,
      action: PayloadAction<ISpaceNumberAndSpaceValidationsResults[]>
    ) => {
      state.spaceValidationRun.spacesValidationsResults = action.payload;
    },
    resetSpaceValidationRunInRedux: (state) => {
      state.spaceValidationRun = {
        active: false,
        loading: false,
        spacesValidationsResults: [],
      };
      state.runSpaceValidationsInSelectedRooms = false;
    },
    setSpaceValidationLoadingInRedux: (state, action: PayloadAction<boolean>) => {
      state.spaceValidationRun.loading = action.payload;
    },
    toggleSpaceValidationSpecInRedux: (state, action: PayloadAction<SPACE_VALIDATION_ID_ENUM>) => {
      state.activeSpaceValidationSpecs[action.payload] =
        !state.activeSpaceValidationSpecs[action.payload];
    },
    setActiveRoomColoringIdInRedux: (
      state,
      action: PayloadAction<ROOM_COLORING_IDS | string | undefined>
    ) => {
      state.activeRoomColoringId = action.payload;
      if (action.payload == null || action.payload === "") {
        localStorage.removeItem("activeRoomColoringId");
      } else {
        localStorage.setItem("activeRoomColoringId", String(action.payload));
      }
    },
    setShowFireDeviceCircles: (state, action: PayloadAction<boolean>) => {
      state.showFireDeviceCircles = action.payload;
    },
    setShowFireDeviceSquare: (state, action: PayloadAction<boolean>) => {
      state.showFireDeviceSquare = action.payload;
    },
    setDeviceOnTheMoveInRedux: (state, action: PayloadAction<IObjectId | undefined>) => {
      state.deviceOnTheMove = action.payload;
    },
    setMeasurementToolActiveInRedux: (state, action: PayloadAction<boolean>) => {
      state.measurementToolActive = action.payload;
    },
    setProjectNoInRedux: (state, action: PayloadAction<number>) => {
      state.projectNo = action.payload;
    },
    addNonPublischedDevicesFromAlgosInRedux: (state, action: PayloadAction<IDevice[]>) => {
      state.nonPublischedDevicesFromAlgos.push(...action.payload);
    },
    removeNonPublischedDevicesFromAlgosInRedux: (state, action: PayloadAction<string[]>) => {
      state.nonPublischedDevicesFromAlgos = state.nonPublischedDevicesFromAlgos.filter(
        (device) => !action.payload.includes(device._id as string)
      );
    },
    clearNonPublischedDevicesFromAlgosInRedux: (state) => {
      state.nonPublischedDevicesFromAlgos = [];
    },
    setMlHeatmapTilesInRedux: (state, action: PayloadAction<MlHeatmapTile[]>) => {
      state.mlHeatmapTiles = action.payload;
    },
    clearMlHeatmapTilesInRedux: (state) => {
      state.mlHeatmapTiles = [];
    },
    setDeviceViewModeInRedux: (state, action: PayloadAction<IDeviceViewMode>) => {
      state.deviceViewMode = action.payload;
      localStorage.setItem("deviceViewMode", action.payload);
    },
    setBuildingStoryListRedux: (state, action: PayloadAction<IBuildingStoryListState>) => {
      state.buildingStoryList = action.payload;
    },
    setActiveModuleInRedux: (state, action: PayloadAction<IMultiplacerModuleKey>) => {
      localStorage.setItem("module", action.payload?.toString() ?? "");
      state.activeModule = action.payload;
    },
    setShowRoomColoringLargeInRedux: (state, action: PayloadAction<boolean>) => {
      state.showRoomColoringLarge = action.payload;
    },
    setRfpStandardRoomDisciplineInRedux: (
      state,
      action: PayloadAction<IRfpStandardRoomDiscipline>
    ) => {
      state.rfpStandardRoomDiscipline = action.payload;
    },
    setRequestZoomExtentInRedux(state) {
      state.zoomExtentNonce += 1;
    },
    resetZoomExtentNonceInRedux(state) {
      state.zoomExtentNonce = 0;
    },
    setMouseHelpModalOpenInRedux(state, action: PayloadAction<boolean>) {
      state.mouseHelpModalOpen = action.payload;
    },
    setPipeSystemsCopyModalOpenInRedux(state, action: PayloadAction<boolean>) {
      state.pipeSystemsCopyModalOpen = action.payload;
    },
    setSelectedRoutingGraphIdInRedux(state, action: PayloadAction<string | null>) {
      state.selectedRoutingGraphId = action.payload;
    },
    setRunSpaceValidationsInSelectedRoomsInRedux: (state, action: PayloadAction<boolean>) => {
      state.runSpaceValidationsInSelectedRooms = action.payload;
    },
    setMcBimItemColorMode: (state, action: PayloadAction<IMcBimItemColorMode>) => {
      state.mcbimItemColorMode = action.payload;
      saveMcBimItemColorMode(state.mcbimItemColorMode);
    },
    setMcBimItemsFilterModeInRedux: (state, action: PayloadAction<IMcBimItemsFilterMode>) => {
      state.mcbimItemsFilterMode = action.payload;
      saveMcBimItemsFilterMode(action.payload);
    },

    setDisciplineToDisplayInRedux: (state, action: PayloadAction<string>) => {
      state.disciplinesToDisplay[action.payload] = !state.disciplinesToDisplay[action.payload];
      saveDisciplinesToDisplay(state.disciplinesToDisplay);
    },
    // Valfritt men ofta praktiskt
    clearDisciplinesToDisplayInRedux: (state) => {
      state.disciplinesToDisplay = {};
      saveDisciplinesToDisplay(state.disciplinesToDisplay);
    },
    setSelectedBuildingInRedux: (state, action: PayloadAction<string | null>) => {
      state.selectedBuilding = action.payload;
    },
    setRfpSpecifiedArticlesResponsibilityInRedux: (state, action: PayloadAction<string | null>) => {
      state.rfpSpecifiedArticlesResponsibility = action.payload;
    },
    setShowContainerSpacesInRedux: (state, action: PayloadAction<boolean>) => {
      state.showContainerSpaces = action.payload;
    },
    setShowOnlyRoomSectionItemsVisibleIn2dInRedux: (state, action: PayloadAction<boolean>) => {
      state.showOnlyRoomSectionItemsVisibleIn2d = action.payload;
    },
    setShowRoomSectionPerimeterItemsInRedux: (state, action: PayloadAction<boolean>) => {
      state.showRoomSectionPerimeterItems = action.payload;
    },
  }),
  selectors: {
    activeTab: (viewer) => viewer.activeTab,
  },
});

export const selectDragSelectSpaces = (state: IRootState) => state.design.dragSelectSpaces;

export const selectMlGenericPredicetedDevices = createSelector(
  [(state: IRootState) => state.design.mlGenrericSpaceDevices],
  (mlGenrericSpaceDevices): IPixPolygon[] => {
    return mlGenrericSpaceDevices.map((mlDevice) => {
      return convertGenereicMlResultToPixPolygon(mlDevice);
    });
  }
);

// selectors
export const selectProjectNo = (state: IRootState) => state.design.projectNo;
export const selectPipeSystemsCopyModalOpen = (state: IRootState) =>
  state.design.pipeSystemsCopyModalOpen;
export const selectSelectedRoutingGraphId = (state: IRootState) =>
  state.design.selectedRoutingGraphId;
export const selectShowOnlyRoomSectionItemsVisibleIn2d = (state: IRootState) =>
  state.design.showOnlyRoomSectionItemsVisibleIn2d;
export const selectShowRoomSectionPerimeterItems = (state: IRootState) =>
  state.design.showRoomSectionPerimeterItems;
export const selectAdvancedCopySelectedTargetRoomNumber = (state: IRootState) =>
  state.design.advancedCopy.selectedTargetRoomNumber;
export const selectAdvancedCopySelectedTargetRoomNumbers = (state: IRootState) =>
  state.design.advancedCopy.selectedTargetRoomNumbers;
const selectFloorAndJob = (state: IRootState) => state.design.floorAndJob;

export const selectGetDevicesArgs = createSelector(
  [selectProjectNo, selectFloorAndJob],
  (projectNo, floorAndJob): PostMultiplacerDevicesApiArg | null => {
    if (!floorAndJob?.floorName) {
      return null;
    }
    return {
      body: {
        projectNo,
        floor: floorAndJob.floorName as string,
        // om du senare vill stödja arrayer:
        // roomNumbers: fileFloorJob.roomNumbers ?? [],
      },
    };
  }
);

// /** Hämtar query-resultatet (senaste i cachen) för nuvarande args */
// export const select = (state: IRootState) => {
//   const args = selectGetRoomsArgs(state);
//   if (!args) return undefined;
//   return openapi.endpoints.postMultiplacerRooms.select(args)(state);
// };

// export const selectIfcDevicesPixPolygons = createSelector([], ():IPixPolygon => {
//   return [];
// }

// Action creators are generated for each case reducer function.
export const {
  setCountAlgoSpaceTotal,
  setCountAlgoSpaceComplete,
  setCountAlgoSpaceFailed,
  setAlgosInProgressInRedux,
  setHidePlayButtonInRedux,
  setToastInRedux,
  setViewerToBeShowInRedux,
  setSelectedSpaceNumberInRedux,
  setSelectedRoomTabInRedux,
  setSelectedSpaceValidationInRedux,
  setActiveLeftSideBarTabInRedux,
  setDragSelectSpacesInRedux,
  setSelectedRoomsInRedux,
  addSelectedRoomToRedux,
  setShiftSelectRoomsInRedux,
  removeSelectedRoomFromRedux,
  toggleReviewModeInRedux,
  incCurrIndexReviewModeInRedux,
  decCurrIndexReviewModeInRedux,
  resetReviewModeInRedux,
  setIfcClassesToDisplayInRedux,
  setIfcClassVisibilityInRedux,
  toggleCeilingIfcClassesContextInRedux,
  setDeviceTypeInstanceClassFilterInRedux,
  setRotationInRedux,
  setCopySpaceModeActiveInRedux,
  setAdvancedCopySelectedTargetRoomNumberInRedux,
  setAdvancedCopySelectedTargetRoomNumbersInRedux,
  toggleShowTrueSizeOfDevicesInRedux,
  toggleSnapToGridInRedux,
  setSpaceValidationsResultsInRedux,
  resetSpaceValidationRunInRedux,
  setSpaceValidationRunActiveInRedux,
  setSpaceValidationLoadingInRedux,
  toggleSpaceValidationSpecInRedux,
  setMultipleSelectedRoomsToRedux,
  setActiveRoomColoringIdInRedux,
  setShowFireDeviceCircles,
  setDeviceOnTheMoveInRedux,
  setMeasurementToolActiveInRedux,
  setProjectNoInRedux,
  addNonPublischedDevicesFromAlgosInRedux,
  clearNonPublischedDevicesFromAlgosInRedux,
  removeNonPublischedDevicesFromAlgosInRedux,
  setMlHeatmapTilesInRedux,
  clearMlHeatmapTilesInRedux,
  setDeviceViewModeInRedux,
  setFloorAndJobInRedux,
  setBuildingStoryListRedux,
  setActiveModuleInRedux,
  setRfpStandardRoomDisciplineInRedux,
  setShowRoomColoringLargeInRedux,
  setRequestZoomExtentInRedux,
  resetZoomExtentNonceInRedux,
  setMouseHelpModalOpenInRedux,
  setPipeSystemsCopyModalOpenInRedux,
  setSelectedRoutingGraphIdInRedux,
  setRunSpaceValidationsInSelectedRoomsInRedux,
  setMcBimItemColorMode,
  setMcBimItemsFilterModeInRedux,
  setDisciplineToDisplayInRedux,
  clearDisciplinesToDisplayInRedux,
  setRfpSpecifiedArticlesResponsibilityInRedux,
  setSelectedBuildingInRedux,
  setShowContainerSpacesInRedux,
  setShowOnlyRoomSectionItemsVisibleIn2dInRedux,
  setShowRoomSectionPerimeterItemsInRedux,
  setShowCeilingFreeTilesForFloorInRedux,
} = designSlice.actions;
